import { Search, X } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RegistryCategory, RegistryGenre, SortBy } from "../../registry";
import { Button } from "../ui/button";

interface MageBookListFiltersProps {
  categories: RegistryCategory[];
  genres: RegistryGenre[];
  showCategories: boolean;
  selectedCategory?: string;
  selectedGenre?: string;
  sortBy?: SortBy;
  query?: string;
  onCategoryChange: (value: string | undefined) => void;
  onGenreChange: (value: string | undefined) => void;
  onSortByChange: (value: SortBy) => void;
  onQueryChange: (value: string) => void;
}

export function SeasonListFilters({
  categories,
  genres,
  showCategories,
  selectedCategory,
  selectedGenre,
  sortBy,
  query,
  onCategoryChange,
  onGenreChange,
  onSortByChange,
  onQueryChange,
}: MageBookListFiltersProps) {
  const [localQuery, setLocalQuery] = useState(query ?? "");

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      {showCategories && (
        <Select
          value={selectedCategory ?? "all"}
          onValueChange={(v) => onCategoryChange(v === "all" ? undefined : v)}
        >
          <SelectTrigger className="w-full sm:w-50" aria-label="Category">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id.toString()}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Select
        value={selectedGenre ?? "all"}
        onValueChange={(v) => onGenreChange(v === "all" ? undefined : v)}
      >
        <SelectTrigger className="w-full sm:w-50" aria-label="Genre">
          <SelectValue placeholder="All genres" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All genres</SelectItem>
          {genres.map((genre) => (
            <SelectItem key={genre.id} value={genre.id.toString()}>
              {genre.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={sortBy ?? "title"}
        onValueChange={(v) => onSortByChange(v as SortBy)}
      >
        <SelectTrigger className="w-full sm:w-50" aria-label="Sort by">
          <SelectValue placeholder="Sort by title" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="title">Sort by title</SelectItem>
          <SelectItem value="-modified">Sort by last modified</SelectItem>
          <SelectItem value="phase">Sort by phase</SelectItem>
        </SelectContent>
      </Select>

      <div className="relative flex-1">
        <Search
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          className="pl-9 pr-9"
          aria-label="Filter AudioGames"
          placeholder="Filter AudioGames by title, genre, tags..."
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onQueryChange(localQuery);
            }
          }}
          onBlur={() => {
            if (localQuery !== (query ?? "")) onQueryChange(localQuery);
          }}
        />
        {localQuery && (
          <Button
            variant="ghost"
            onClick={() => {
              setLocalQuery("");
              onQueryChange("");
            }}
            className="absolute right-0 top-1/2 -translate-y-1/2"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
}
